import { 
  collection, 
  addDoc, 
  doc, 
  getDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import type { CallDoc, CreateCallParams, AcceptCallParams, CallType } from '../types/call';
import { getFamilyPlanLimits } from './subscriptions';

const CALL_TIMEOUT_MS = 30000; // 30 segundos
const MAX_RETRIES = 4;
const RETRY_INTERVAL_MS = 10000; // 10 segundos

/**
 * Obtiene devices elegibles para recibir la llamada
 * - type='home': devices con isHomeDevice=true y lastSeenAt <= 30 días
 * - type='member': devices del targetMemberId con lastSeenAt <= 30 días
 */
async function getEligibleDevices(
  familyId: string,
  type: CallType,
  targetMemberId: string | null
): Promise<Array<{ deviceId: string; pushToken: string | null; memberId: string }>> {
  const devicesRef = collection(db, 'devices');
  const now = Date.now();
  const cutoff = now - 30 * 24 * 60 * 60 * 1000; // 30 días

  let q;
  if (type === 'home') {
    q = query(
      devicesRef,
      where('familyId', '==', familyId),
      where('isHomeDevice', '==', true)
    );
  } else {
    if (!targetMemberId) return [];
    q = query(
      devicesRef,
      where('familyId', '==', familyId),
      where('memberId', '==', targetMemberId)
    );
  }

  const snapshot = await getDocs(q);
  const eligible: Array<{ deviceId: string; pushToken: string | null; memberId: string }> = [];
  const eligiblePersonal: Array<{ deviceId: string; pushToken: string | null; memberId: string }> = [];
  const eligiblePrimary: Array<{ deviceId: string; pushToken: string | null; memberId: string }> = [];

  snapshot.docs.forEach(docSnap => {
    const data = docSnap.data();
    const lastSeenAt = data.lastSeenAt;
    const lastSeenMs = lastSeenAt instanceof Timestamp 
      ? lastSeenAt.toMillis() 
      : typeof lastSeenAt === 'number' 
      ? lastSeenAt 
      : 0;

    if (lastSeenMs >= cutoff && data.pushToken) {
      const item = {
        deviceId: docSnap.id,
        pushToken: data.pushToken,
        memberId: data.memberId || ''
      };
      eligible.push(item);

      if (type === 'member' && data.isHomeDevice !== true) {
        eligiblePersonal.push(item);

        if (data.isPrimaryForUser === true) {
          eligiblePrimary.push(item);
        }
      }
    } else if (type === 'member') {
      console.log('📞 Device descartado (sin token o inactivo):', {
        deviceId: docSnap.id,
        hasPushToken: !!data.pushToken,
        lastSeenMs,
        cutoff,
        isHomeDevice: data.isHomeDevice,
        isPrimaryForUser: data.isPrimaryForUser,
      });
    }
  });

  // Para llamadas a un miembro, priorizar:
  // 1) Dispositivos marcados como "principal" (isPrimaryForUser)
  // 2) Dispositivos personales (no-casa)
  // 3) Todos los dispositivos del miembro (fallback)
  if (type === 'member') {
    if (eligiblePrimary.length > 0) {
      console.log('📞 Usando dispositivos primarios:', eligiblePrimary.length);
      return eligiblePrimary;
    }
    if (eligiblePersonal.length > 0) {
      console.log('📞 Usando dispositivos personales (no-casa):', eligiblePersonal.length);
      return eligiblePersonal;
    }
    console.log('📞 Fallback: usando todos los dispositivos del miembro:', eligible.length);
  }

  // Fallback: si type='home' y no hay home devices elegibles, notificar todos los devices elegibles de la familia
  if (type === 'home' && eligible.length === 0) {
    const fallbackQuery = query(
      devicesRef,
      where('familyId', '==', familyId)
    );
    const fallbackSnap = await getDocs(fallbackQuery);
    fallbackSnap.docs.forEach((docSnap) => {
      const data = docSnap.data();
      const lastSeenAt = data.lastSeenAt;
      const lastSeenMs =
        lastSeenAt instanceof Timestamp
          ? lastSeenAt.toMillis()
          : typeof lastSeenAt === 'number'
            ? lastSeenAt
            : 0;

      if (lastSeenMs >= cutoff && data.pushToken) {
        eligible.push({
          deviceId: docSnap.id,
          pushToken: data.pushToken,
          memberId: data.memberId || '',
        });
      }
    });
  }

  return eligible;
}

/**
 * Envía push notification a los devices elegibles
 * TODO: Integrar con servicio de push notifications (FCM/APNs)
 */
async function sendPushNotifications(
  devices: Array<{ deviceId: string; pushToken: string | null; memberId: string }>,
  callId: string,
  callerName: string,
  type: CallType,
  targetMemberName: string | null
): Promise<void> {
  // TODO: Implementar envío real de push notifications
  // Por ahora solo log
  console.log('📞 Enviando push notifications:', {
    devices: devices.length,
    callId,
    callerName,
    type,
    targetMemberName
  });

  // Ejemplo de payload para push notification:
  // {
  //   title: type === 'home' ? 'Llamada a Casa' : `Llamada de ${callerName}`,
  //   body: type === 'home' 
  //     ? `${callerName} está llamando a casa` 
  //     : `${callerName} te está llamando`,
  //   data: {
  //     type: 'incoming_call',
  //     callId,
  //     link: `/calls/incoming/${callId}`
  //   }
  // }
}

/**
 * Crea una nueva llamada y envía notificaciones a devices elegibles
 */
export async function createCall(params: CreateCallParams): Promise<{ callId: string; success: boolean; error?: string }> {
  try {
    const { familyId, callerMemberId, callerMemberName, callerDeviceId, type, targetMemberId, targetMemberName } = params;

    // Validación de plan: si las llamadas están bloqueadas, no permitir crear llamadas
    try {
      const planLimits = await getFamilyPlanLimits(familyId);
      if (Number((planLimits as any)?.familyCalls) === -1) {
        return { callId: '', success: false, error: 'Tu plan no incluye llamadas' };
      }
    } catch {
      // Si no se puede verificar el plan, no bloqueamos aquí (evitar falsos negativos)
    }

    // Validar parámetros
    if (type === 'member' && !targetMemberId) {
      return { callId: '', success: false, error: 'targetMemberId is required for member calls' };
    }

    // Obtener devices elegibles
    const eligibleDevices = await getEligibleDevices(familyId, type, targetMemberId || null);

    if (eligibleDevices.length === 0) {
      return { callId: '', success: false, error: 'No eligible devices found' };
    }

    // Crear documento de llamada
    const now = Date.now();
    const expiresAt = new Date(now + CALL_TIMEOUT_MS);
    const nextRetryAt = new Date(now + RETRY_INTERVAL_MS);

    const callData: Omit<CallDoc, 'id'> = {
      familyId,
      callerMemberId,
      callerMemberName,
      callerDeviceId,
      type,
      targetMemberId: targetMemberId || null,
      targetMemberName: targetMemberName || null,
      targetDeviceIds: eligibleDevices.map((d) => d.deviceId),
      status: 'ringing',
      acceptedByDeviceId: null,
      acceptedByMemberId: null,
      acceptedByMemberName: null,
      retryCount: 0,
      maxRetries: MAX_RETRIES,
      retryIntervalMs: RETRY_INTERVAL_MS,
      nextRetryAt: Timestamp.fromDate(nextRetryAt),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      expiresAt: Timestamp.fromDate(expiresAt)
    };

    const callRef = await addDoc(collection(db, 'calls'), callData);

    // Enviar push notifications
    await sendPushNotifications(
      eligibleDevices,
      callRef.id,
      callerMemberName,
      type,
      targetMemberName || null
    );

    return { callId: callRef.id, success: true };
  } catch (error) {
    console.error('Error creating call:', error);
    return { 
      callId: '', 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Acepta una llamada (first device wins)
 */
export async function acceptCall(params: AcceptCallParams): Promise<{ success: boolean; error?: string }> {
  try {
    const { callId, deviceId, memberId, memberName } = params;

    const callRef = doc(db, 'calls', callId);
    const callSnap = await getDoc(callRef);

    if (!callSnap.exists()) {
      return { success: false, error: 'Call not found' };
    }

    const callData = callSnap.data() as CallDoc;

    // Verificar que la llamada esté en estado 'ringing'
    if (callData.status !== 'ringing') {
      return { success: false, error: `Call is already ${callData.status}` };
    }

    // Actualizar a 'accepted' (first device wins)
    await updateDoc(callRef, {
      status: 'accepted',
      acceptedByDeviceId: deviceId,
      acceptedByMemberId: memberId,
      acceptedByMemberName: memberName,
      acceptedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error accepting call:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Rechaza una llamada
 */
export async function rejectCall(callId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const callRef = doc(db, 'calls', callId);
    const callSnap = await getDoc(callRef);

    if (!callSnap.exists()) {
      return { success: false, error: 'Call not found' };
    }

    const callData = callSnap.data() as CallDoc;

    if (callData.status !== 'ringing') {
      return { success: false, error: `Call is already ${callData.status}` };
    }

    await updateDoc(callRef, {
      status: 'rejected',
      rejectedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error rejecting call:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Cancela una llamada (caller cancels)
 */
export async function cancelCall(callId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const callRef = doc(db, 'calls', callId);
    await updateDoc(callRef, {
      status: 'cancelled',
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error cancelling call:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Obtiene información de una llamada
 */
export async function getCall(callId: string): Promise<CallDoc | null> {
  try {
    const callRef = doc(db, 'calls', callId);
    const callSnap = await getDoc(callRef);

    if (!callSnap.exists()) {
      return null;
    }

    return { id: callSnap.id, ...callSnap.data() } as CallDoc & { id: string };
  } catch (error) {
    console.error('Error getting call:', error);
    return null;
  }
}
